import { inject, Injectable, signal } from '@angular/core';
import { MessagesUiService } from '@okcargo-apps/messages-ui';
import {
  EStopType,
  getEmptyFreightDetailForShipper,
  getEmptyIStopAddress,
  getEmptyStopDetailedForShipper,
  IFreightDetailedForShipper,
  IStopDetailedForShipper,
  UUID,
  UUID_NULL,
  validateAndCorrectStopsForShipper,
} from '@okcargo-apps/shared-domain';
import { FreightShipperTemporaryStoreService } from './freight-shipper-temporary-store.service';
import { FreightShipperValidationMessagesAdapterService } from './freight-shipper-validation-messages-adapter.service';

@Injectable({
  providedIn: 'root',
})
export class FreightShipperFormsService {
  // TODO save data from freight
  private readonly _freight = signal<IFreightDetailedForShipper>(getEmptyFreightDetailForShipper());
  public readonly freight = this._freight.asReadonly();

  private readonly freightShipTemporaryStoreService = inject(FreightShipperTemporaryStoreService);

  private readonly messagesUiService = inject(MessagesUiService);
  private readonly freightShipValidationMessagesAdapterService = inject(FreightShipperValidationMessagesAdapterService);

  private readonly _hasValidationErrors = signal(false);
  public readonly hasValidationErrors = this._hasValidationErrors.asReadonly();

  // constructor() {}

  public isFreightInStore(freightId: UUID): boolean {
    return this.freightShipTemporaryStoreService.has(freightId);
  }

  public getFromStore(freightId: UUID): void {
    const freight = this.freightShipTemporaryStoreService.get(freightId);
    this.updateFreightSignal(freight.stopsDetailed, freight);
  }

  public removeFromStore(freightId: UUID): void {
    this.freightShipTemporaryStoreService.delete(freightId);
  }

  public setCurrentFreight(freight: IFreightDetailedForShipper): void {
    // TODO buscar en el store si había uno sin guardar??????
    this.updateFreightSignal(freight.stopsDetailed, freight);
  }

  public addStop(stopType: EStopType = EStopType.UNLOAD): UUID {
    const currentStops = this._freight().stopsDetailed;
    const stop = this.createStop(stopType, currentStops.length);

    this.updateFreightSignal([...currentStops, stop]);

    return stop.id;
  }

  public removeStop(stopId: UUID): void {
    const currentStops = this._freight().stopsDetailed;

    if (currentStops.length <= 2) {
      return;
    }

    const stopToRemove = currentStops.find((s) => s.id === stopId);

    if (!stopToRemove) return;

    const positionToRemove = stopToRemove.position;

    const updatedStops = currentStops
      .filter((stop) => stop.id !== stopId)
      .map((stop) => {
        if (stop.position > positionToRemove) {
          return { ...stop, position: stop.position - 1 };
        }
        return stop;
      });

    this.updateFreightSignal(updatedStops);
  }

  public reorderStops(stopId: UUID, newPosition: number): void {
    const currentStops = this._freight().stopsDetailed;
    if (newPosition < 0 || newPosition >= currentStops.length) {
      return;
    }

    const stopIndex = currentStops.findIndex((s) => s.id === stopId);
    if (stopIndex === -1) return;

    const reorderedStops = [...currentStops];
    const [removedStop] = reorderedStops.splice(stopIndex, 1);
    reorderedStops.splice(newPosition, 0, removedStop);

    const updatedStops = reorderedStops.map((stop, index) => ({
      ...stop,
      position: index,
    }));

    this.updateFreightSignal(updatedStops);
  }

  public updateStop(stopId: UUID, updatedData: Partial<IStopDetailedForShipper>): void {
    const currentStops = this._freight().stopsDetailed;
    const updatedStops = currentStops.map((stop) => (stop.id === stopId ? { ...stop, ...updatedData } : stop));
    this.updateFreightSignal(updatedStops);
  }

  public updateGeneralData(generalData: IFreightDetailedForShipper): void {
    const currentFreight = this._freight();

    const updatedFreight: IFreightDetailedForShipper = {
      ...currentFreight,
      reference: generalData.reference,
      vehicleTypes: generalData.vehicleTypes,
      loadingTypes: generalData.loadingTypes,
      deliveryNoteInPaperRequired: generalData.deliveryNoteInPaperRequired,
      waste: generalData.waste,
      instructions: generalData.instructions,
      // temperatureMin: generalData.temperatureMin,
      // temperatureMax: generalData.temperatureMax,
      // temperatureUnit: generalData.temperatureUnit,
      shipperSiteId: generalData.shipperSiteId,
    };

    this.updateFreightSignal(updatedFreight.stopsDetailed, updatedFreight);
  }

  public resetAll(): void {
    this._hasValidationErrors.set(false);
    if (this.messagesUiService.getMessagesForObjectId(UUID_NULL).length > 0) {
      this.messagesUiService.removeMessageByCriteria({
        objectId: UUID_NULL,
      });
    }

    // initialize with default stops
    const firstStop = this.createStop(EStopType.LOAD, 0);
    const lastStop = this.createStop(EStopType.UNLOAD, 1);
    const newFreight = getEmptyFreightDetailForShipper();

    this._freight.set({
      ...newFreight,
      id: UUID_NULL,
      stopsDetailed: [firstStop, lastStop],
      numberOfStops: 2,
    });

    /*
    No se deben almacenar estos cambios en el store. Simplemente borra los datos existentes de la UI
    antes de cargar información
    */
  }

  //-- private

  private createStop(stopType: EStopType, position: number): IStopDetailedForShipper {
    // const freightId = this.freight().id;

    const stop = getEmptyStopDetailedForShipper();
    // stop.freightId = freightId;
    stop.position = position;
    stop.id = crypto.randomUUID();
    stop.type = stopType;
    stop.address = getEmptyIStopAddress();

    return stop;
  }

  /*
  Único punto en el que se actualiza o modifica el signal, no debe haber ningún otro set o update del
  signal en el código del servicio. Siempre ha de pasarse por aquí si hay cambios en los datos.
  De esta forma nos aseguramos que:
  - Los datos del store y del signal son consistentes
  - Siempre se ejecutan las correcciones automáticas y validaciones
  - El signal siempre emite, ya que se almacena una copia que sustituye al objeto anterior (inmutabilidad superficial)
  */

  private updateFreightSignal(stops: IStopDetailedForShipper[], modifiedFreight?: IFreightDetailedForShipper): void {
    // correctedStops it's a shallow copy
    const { result, correctedStops } = validateAndCorrectStopsForShipper(stops);

    this._hasValidationErrors.set(result.hasErrors);

    const freight = modifiedFreight ? modifiedFreight : this._freight();

    this._freight.set({
      ...freight,
      stopsDetailed: correctedStops,
    });

    this.freightShipTemporaryStoreService.createOrUpdate(this._freight());

    const allStopIds = correctedStops.map((stop) => stop.id);

    this.freightShipValidationMessagesAdapterService.notifyMessages(result, allStopIds);

    // console.log('updateFreightSignal()');
  }
}
